import { Info } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useRequiredTooltip } from './required-tooltip.context';

interface RequiredFieldTooltipProps {
  /** Só exibe o tooltip quando o campo é obrigatório. */
  active?: boolean;
  /** Texto do balão. */
  text?: string;
  /**
   * Quando muda, o balão "pisca" por 2s (além da intro/hover). Usado para
   * abrir o tooltip da Franquia assim que o LMI da cobertura é preenchido.
   */
  flashKey?: string | number;
  /** Classes extras no wrapper (ex.: `flex-1` para preservar layout flex). */
  className?: string;
  children: ReactNode;
}

/**
 * Envolve um campo obrigatório e exibe um balão "Dado obrigatório" acima dele.
 * Visível durante a introdução (todos ao entrar na página), no hover do campo e
 * sempre que `flashKey` muda. Sem provider ou sem `active`, é um passthrough.
 */
export function RequiredFieldTooltip({
  active,
  text = 'Dado obrigatório',
  flashKey,
  className,
  children,
}: RequiredFieldTooltipProps) {
  const ctx = useRequiredTooltip();
  const [hovered, setHovered] = useState(false);
  const [flashed, setFlashed] = useState(false);

  useEffect(() => {
    if (flashKey === undefined) return;
    setFlashed(true);
    const timer = setTimeout(() => setFlashed(false), 2000);
    return () => clearTimeout(timer);
  }, [flashKey]);

  // Fora do provider ou campo não obrigatório → não renderiza o balão.
  if (!ctx || !active) return <>{children}</>;

  const visible = ctx.introActive || hovered || flashed;

  return (
    <div
      className={`relative ${className ?? ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        role='tooltip'
        aria-hidden={!visible}
        className={`pointer-events-none absolute bottom-full left-0 z-30 transition-all duration-200 ease-out ${
          visible ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0'
        }`}
      >
        <div className='flex items-center gap-2 whitespace-nowrap rounded-2xl bg-alba-blue-500 px-4 py-2.5 text-sm font-medium text-white shadow-[0px_8px_24px_rgba(15,22,30,0.24)]'>
          <Info className='h-4 w-4 shrink-0' strokeWidth={2} />
          <span>{text}</span>
        </div>
        {/* seta apontando para baixo (perto do início) */}
        <div className='absolute left-6 top-full h-0 w-0 border-x-[7px] border-t-[7px] border-x-transparent border-t-alba-blue-500' />
      </div>
      {children}
    </div>
  );
}
